import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

// Show links (latest free half-episode lives on these).
const SPOTIFY_SHOW_URL = 'https://open.spotify.com/show/2jU246e6bhY0ML848jUHQz';
const APPLE_PODCASTS_URL =
    'https://podcasts.apple.com/sk/podcast/g%C3%B3ly-a-es%C3%A1-futbalov%C3%A9-anal%C3%BDzy-value-predikcie/id1879616734';

// --- Styled Components ---

const Header = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 10px;
    width: 100%;
    max-width: 600px;
    box-sizing: border-box;
`;

const Logo = styled.img`
    width: 100px;
    height: 100px;
    border-radius: 50%;
    margin-top: 20px;
`;

const CommunityName = styled.h1`
    font-size: clamp(1.5rem, 5vw, 2rem);
    margin-top: 14px;
    margin-bottom: 0;
    text-align: center;
`;

// Centered lead line under the title.
const Lead = styled.p`
    font-size: 0.95em;
    line-height: 1.5;
    color: #4b5159;
    margin: 12px auto 0;
    padding: 0 20px;
    max-width: 460px;
    text-align: center;
`;

// The native home feed intentionally omits this web introduction.
const Sections = styled.div`
    width: 100%;
    max-width: 480px;
    margin: 22px auto 0;
    padding: 0 20px;
    text-align: left;
    box-sizing: border-box;
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const SectionTitle = styled.h2`
    font-size: 1.05em;
    font-weight: 800;
    color: #15171b;
    margin: 0;
`;

const SectionBody = styled.p`
    font-size: 0.9em;
    line-height: 1.5;
    color: #5b626c;
    margin: 0;
`;

const Divider = styled.div`
    height: 1px;
    background-color: #e7e9ed;
    margin: 16px 0;
`;

const SpotifyLink = styled.a`
    color: #1db954;
    font-weight: 600;
    text-decoration: none;
    &:hover { text-decoration: underline; }
`;

const AppleLink = styled.a`
    color: #9b2fe6;
    font-weight: 600;
    text-decoration: none;
    &:hover { text-decoration: underline; }
`;

const actionStyle = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    min-height: 44px;
    padding: 11px 16px;
    border: 1px solid #c84e00;
    border-radius: 8px;
    color: #a64000;
    font-size: 0.95em;
    font-weight: 700;
    line-height: 1.4;
    text-align: center;
    text-decoration: none;
    &:hover { background-color: #fff0e5; }
    &:focus-visible { outline: 3px solid #15171b; outline-offset: 3px; }
`;

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 16px;
    & > a { flex: 1 1 180px; }
`;

const ActionLink = styled.a`
    ${actionStyle}
`;

const MembershipLink = styled(ActionLink)`
    background-color: #c84e00;
    color: #fff;
    &:hover { background-color: #a64000; }
`;

const DbLink = styled(Link)`
    ${actionStyle}
    align-self: flex-start;
    margin-top: 12px;
`;

const ResultsSummary = styled.p`
    margin: 6px 0;
    color: #15171b;
    font-size: 0.9em;
    font-weight: 700;
    line-height: 1.5;
`;

// --- Component ---

const ProfileHeader = () => {
    return (
        <Header>
            <Logo src="/g.png" alt="Góly a Esá" />

            <CommunityName>Góly a Esá – miesto pre futbalových fanúšikov ⚽️</CommunityName>

            <Lead>
                Futbal nás baví už roky. Rozoberáme ho, diskutujeme o ňom a naše pohľady
                premieňame na analýzy, predikcie a tipy.
            </Lead>

            <Sections>
                <Section>
                    <SectionTitle>🎙️ Nový podcast každý týždeň</SectionTitle>
                    <SectionBody>
                        Rozbory zápasov, preview ďalších kôl a aktuálne futbalové témy.
                    </SectionBody>
                </Section>

                <Divider />

                <Section>
                    <SectionTitle>📲 S členstvom získaš</SectionTitle>
                    <SectionBody>
                        Plné epizódy podcastu, naše analýzy a krátkodobé aj dlhodobé tikety.
                    </SectionBody>
                </Section>

                <Divider />

                <Section>
                    <SectionTitle>🎧 Najprv si nás vypočuj zadarmo</SectionTitle>
                    <SectionBody>
                        Polovica každej epizódy je dostupná na{' '}
                        <SpotifyLink href={SPOTIFY_SHOW_URL} target="_blank" rel="noopener noreferrer">
                            Spotify
                        </SpotifyLink>{' '}
                        a{' '}
                        <AppleLink href={APPLE_PODCASTS_URL} target="_blank" rel="noopener noreferrer">
                            Apple Podcasts
                        </AppleLink>
                        .
                    </SectionBody>
                    <Actions>
                        <ActionLink href="#podcast-zadarmo">Vypočuť zadarmo</ActionLink>
                        <MembershipLink href="#clenstvo">Získať plný prístup</MembershipLink>
                    </Actions>
                </Section>

                <Divider />

                <Section>
                    <SectionTitle>📊 Naše výsledky</SectionTitle>
                    <ResultsSummary>750+ zverejnených tipov • 3. sezóna projektu</ResultsSummary>
                    <SectionBody>
                        Výsledky našich tipov si môžeš pozrieť v kompletnej databáze.
                    </SectionBody>
                    <DbLink to="/databaza-tipov">Pozrieť databázu</DbLink>
                </Section>
            </Sections>
        </Header>
    );
};

export default ProfileHeader;
